import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cn } from "./cn";

/** True when the user has requested reduced motion (accessibility). */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return reduced;
}

export interface RevealProps {
  children: ReactNode;
  /** ms delay before the animation runs (handy for staggering a list). */
  delay?: number;
  /** Animate only the first time it enters the viewport (default true). */
  once?: boolean;
  className?: string;
}

/**
 * Fade + rise a block into view when it scrolls into the viewport.
 * Uses IntersectionObserver + the `fadeUp` keyframe. When the user prefers
 * reduced motion, children render immediately with no animation.
 */
export function Reveal({ children, delay = 0, once = true, className }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = usePrefersReducedMotion();
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (reduced) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown(true);
            if (once) io.disconnect();
          } else if (!once) {
            setShown(false);
          }
        });
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reduced, once]);

  return (
    <div
      ref={ref}
      className={cn(
        reduced ? "opacity-100" : shown ? "motion-safe:animate-fadeUp" : "opacity-0",
        className,
      )}
      style={shown && !reduced && delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}
